import { NODE_BRANCHES } from './types.ts'
import type { FlowGraph, FlowNode, NodeBranch, NodePosition } from './types.ts'

/**
 * The canvas's model, and the two pure functions that convert to and from the
 * stored graph (D9).
 *
 * They live here rather than in the console for one reason: the console has no
 * test runner, so anything with a rule in it belongs where a test can reach it.
 * What is left in `features/flows/Canvas/` is rendering and pointer handling.
 *
 * **Arrows are derived, never stored twice.** A graph holds `resolve`/`reject`
 * pointers; the canvas wants edges. Storing both would give two answers to
 * "where does this branch go" and one of them would eventually be stale.
 */

/** One derived arrow. `id` is a function of the edge, so it never needs minting. */
export interface FlowEdge {
  id: string
  source: string
  target: string
  branch: NodeBranch
}

export interface FlowDraft {
  nodes: readonly FlowNode[]
  edges: readonly FlowEdge[]
  retiredKeys: readonly string[]
}

export const edgeId = (source: string, branch: NodeBranch): string => `${source}:${branch}`

/** The stored graph as the canvas wants it: nodes, plus the arrows they imply. */
export function fromGraph(graph: FlowGraph): FlowDraft {
  const edges: FlowEdge[] = []
  for (const node of graph.nodes) {
    for (const branch of NODE_BRANCHES) {
      const target = node[branch]
      if (target === null) continue
      edges.push({ id: edgeId(node.id, branch), source: node.id, target, branch })
    }
  }
  return { nodes: graph.nodes, edges, retiredKeys: graph.retiredKeys ?? [] }
}

/**
 * The canvas's model folded back into a storable graph.
 *
 * A branch that carries more than one edge keeps the **first**; the canvas only
 * offers one connection per handle, so a second is a bug in the draft rather
 * than a choice to preserve, and the save re-validates the result either way.
 * Losing the extra here is what keeps the stored graph single-valued — the
 * property the whole `resolve`/`reject` shape rests on.
 *
 * `toGraph(fromGraph(g))` is `g`, which is the property the round-trip test
 * pins.
 */
export function toGraph(draft: FlowDraft): FlowGraph {
  const taken = new Map<string, string>()
  for (const edge of draft.edges) {
    const slot = edgeId(edge.source, edge.branch)
    if (!taken.has(slot)) taken.set(slot, edge.target)
  }

  const nodes = draft.nodes.map((node) => ({
    ...node,
    resolve: taken.get(edgeId(node.id, 'resolve')) ?? null,
    reject: taken.get(edgeId(node.id, 'reject')) ?? null,
  }))

  return draft.retiredKeys.length > 0 ? { nodes, retiredKeys: [...draft.retiredKeys] } : { nodes }
}

/**
 * Where a new child of `parent` goes: the resolve child to the right, the
 * reject child a row below it.
 *
 * Directus has a placement heuristic and no auto-layout (study §8), and the
 * same trade is taken here — an auto-layout that moves a node a person placed
 * is worse than a first guess they can drag. The one addition is that the guess
 * never lands on an occupied cell: two parents can otherwise put their children
 * on top of each other, and the second one appears not to have been created.
 */
export function positionForChild(
  parent: FlowNode,
  branch: NodeBranch,
  graph: FlowGraph,
): NodePosition {
  return freeCellFrom(graph, {
    x: parent.position.x + 1,
    y: parent.position.y + (branch === 'reject' ? 1 : 0),
  })
}

/**
 * The first unoccupied cell at or below `from`, scanning one row at a time.
 *
 * It lives here rather than in the canvas because it is a placement *rule*, and
 * this module's own header gives the reason: the console has no test runner, so
 * anything with a rule in it belongs where a test can reach it. It had been
 * written twice — once here inside {@link positionForChild} and once in the
 * canvas's store for Add and Duplicate — and two copies of a scan direction is
 * exactly the pair that drifts the first time it changes.
 */
export function freeCellFrom(graph: FlowGraph, from: NodePosition): NodePosition {
  const occupied = new Set(graph.nodes.map((n) => `${n.position.x},${n.position.y}`))
  let y = from.y
  while (occupied.has(`${from.x},${y}`)) y += 1
  return { x: from.x, y }
}

/**
 * Would connecting `source` to `target` close a loop? — the editor's own
 * refusal, at the moment of the drop (D9, study §8).
 *
 * It is a *separate* answer from `validateFlowGraph`'s `cycle` issue (`./validate.ts`) and
 * both are wanted. The validator judges a graph that already exists, which is
 * the right shape for the save; this judges an edge that does not exist yet, so
 * a person is never handed a canvas they have to repair before it can be
 * stored. Directus refuses the same drop for the same reason and re-validates
 * on the server, and this keeps that pair.
 *
 * The walk starts at `target` and follows `resolve`/`reject`: the new edge
 * closes a loop exactly when `source` is already reachable from `target`. A
 * self-edge is the degenerate case of that and is refused by the same test
 * rather than by a special one.
 *
 * A graph that is *already* cyclic cannot make this hang — `seen` bounds the
 * walk at one visit per node — and it answers about the proposed edge only, so
 * an existing loop elsewhere neither hides nor invents this one.
 */
export function isLoop(graph: FlowGraph, source: string, target: string): boolean {
  if (source === target) return true

  const byId = new Map(graph.nodes.map((n) => [n.id, n]))
  const seen = new Set<string>()
  const queue = [target]
  while (queue.length > 0) {
    const id = queue.shift() as string
    if (id === source) return true
    if (seen.has(id)) continue
    seen.add(id)
    const node = byId.get(id)
    for (const branch of NODE_BRANCHES) {
      const next = node?.[branch]
      if (next !== null && next !== undefined) queue.push(next)
    }
  }
  return false
}
